<script setup lang="ts">
// The app's fonts, for admins: one for headings, one for all other text. A font file is
// uploaded, or linked (the server fetches it once and serves it itself, so nobody's browser
// contacts the linked site). Unset, the app's own: Lilex.
import { onMounted, ref } from 'vue'
import { describeError } from '../api/http'
import type { ServerFonts } from '../api/types'
import { applyFonts } from '../lib/fonts'
import { useSession } from '../stores/session'

type Role = 'heading' | 'text'

const session = useSession()
const fonts = ref<ServerFonts>({})
const busy = ref<Role | null>(null)
const linking = ref<Role | null>(null)
const linkUrl = ref('')
const inputs: Record<Role, HTMLInputElement | null> = { heading: null, text: null }

const roles: { role: Role; label: string; hint: string; sample: string }[] = [
  { role: 'heading', label: 'Headings', hint: 'Space, channel and page titles', sample: 'Friday film night' },
  { role: 'text', label: 'Text', hint: 'Messages, menus, labels and everything else', sample: 'See you at 8 · 0123456789' },
]

onMounted(async () => {
  try {
    fonts.value = (await session.api.instance()).fonts ?? {}
  } catch (e) {
    session.toast(describeError(e), 'error')
  }
})

async function run(role: Role, fn: () => Promise<ServerFonts>, ok: string): Promise<void> {
  busy.value = role
  try {
    fonts.value = await fn()
    applyFonts(fonts.value)
    session.toast(ok)
  } catch (e) {
    session.toast(describeError(e), 'error')
  } finally {
    busy.value = null
  }
}

function onFile(role: Role, input: HTMLInputElement): void {
  const file = input.files?.[0]
  input.value = ''
  if (file) void run(role, () => session.api.setFont(role, file), `${file.name} is the ${role} font`)
}

function saveLink(role: Role): void {
  const url = linkUrl.value.trim()
  if (!url) return
  void run(role, () => session.api.linkFont(role, url), 'Font fetched and saved on this server').then(() => {
    if (fonts.value[role]?.source === url) {
      linking.value = null
      linkUrl.value = ''
    }
  })
}

const reset = (role: Role) => run(role, () => session.api.resetFont(role), 'Back to Lilex')

const host = (url: string) => {
  try {
    return new URL(url).host
  } catch {
    return url
  }
}
</script>

<template>
  <section class="border-t border-border px-6 py-5" data-testid="font-settings">
    <h2 class="label">Fonts</h2>
    <p class="mono mb-4 text-text-muted">
      One font for headings, one for everything else. Upload a WOFF2, WOFF, TTF or OTF file (up to 4 MB), or link one:
      the server fetches it once and serves it itself, so people's browsers never contact another site. Make sure its
      licence lets you use it this way (Google Fonts and other open-licence fonts do). Code always stays monospaced.
    </p>
    <div class="grid gap-px border border-border bg-border md:grid-cols-2">
      <div v-for="r in roles" :key="r.role" class="bg-bg-primary p-4" :data-testid="`font-${r.role}`">
        <div class="mb-1 flex items-baseline justify-between gap-3">
          <h3 class="label mb-0">{{ r.label }}</h3>
          <span class="mono truncate text-text-muted" :title="fonts[r.role]?.source" :data-testid="`font-${r.role}-name`">
            {{ fonts[r.role]?.name ?? 'Lilex' }}
            <template v-if="fonts[r.role]?.source"> · from {{ host(fonts[r.role]!.source!) }}</template>
            <template v-else-if="!fonts[r.role]"> · default</template>
          </span>
        </div>
        <p class="mono mb-3 text-text-disabled">{{ r.hint }}</p>
        <p
          class="mb-4 truncate"
          :class="r.role === 'heading' ? 'text-3xl font-light tracking-tight' : 'text-base'"
          :style="{ fontFamily: r.role === 'heading' ? 'var(--font-heading)' : 'var(--font-sans)' }"
        >
          {{ r.sample }}
        </p>
        <form v-if="linking === r.role" class="flex gap-2" @submit.prevent="saveLink(r.role)">
          <input
            v-model="linkUrl"
            type="url"
            required
            class="input min-w-0 flex-1"
            placeholder="https://…/font.woff2"
            aria-label="Font file URL"
            :data-testid="`font-${r.role}-url`"
          />
          <button class="btn-primary" :disabled="busy === r.role" :data-testid="`font-${r.role}-link-save`">Fetch</button>
          <button type="button" class="btn-ghost" @click="linking = null">Cancel</button>
        </form>
        <div v-else class="flex flex-wrap gap-2">
          <button class="btn-outline" :disabled="busy === r.role" :data-testid="`font-${r.role}-upload`" @click="inputs[r.role]?.click()">
            {{ busy === r.role ? '…' : 'Upload' }}
          </button>
          <input
            :ref="(el) => (inputs[r.role] = el as HTMLInputElement | null)"
            type="file"
            accept=".woff2,.woff,.ttf,.otf,font/woff2,font/woff,font/ttf,font/otf"
            class="hidden"
            :data-testid="`font-${r.role}-file`"
            @change="onFile(r.role, $event.target as HTMLInputElement)"
          />
          <button class="btn-ghost" :disabled="busy === r.role" :data-testid="`font-${r.role}-link`" @click="((linking = r.role), (linkUrl = ''))">Link</button>
          <button
            v-if="fonts[r.role]"
            class="btn-ghost"
            :disabled="busy === r.role"
            :data-testid="`font-${r.role}-reset`"
            @click="reset(r.role)"
          >
            Use default
          </button>
        </div>
      </div>
    </div>
  </section>
</template>
